import { FC, useEffect, useState, useRef } from 'react';
import { useLoaderData } from 'react-router-dom';
import { ITabState } from '../../../states/tabState.model';
import { useTranslation } from 'react-i18next';
import { GridAggregationFunction, GridSortModel } from '@mui/x-data-grid-premium';
import { localeFormatterHelper } from '../../../../../../shared/helpers/formatter/localeFormatter.helper';
import DOMPurify from 'dompurify';
import {
  articleOverviewState,
  getInitSortModel,
  OverviewArticleData,
} from './articlesOverview.state.tsx';
import { SideControls } from './components/sideControls.component';
import { Column } from '../../../../../../shared/components/table/components/common.components';
import { storageHelper } from '../../../../../../shared/helpers/storage';
import { GroupTable } from '../../../../../../shared/components/groupTable/groupTable.component';
import {
  customSumAggregation,
  renderCellForGroupTable,
} from '../../../../../../shared/components/groupTable/functions/forStyle';
import { Link, Typography } from '@mui/material';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { navigateService } from '../../../../../../shared/services/navigate/navigate.service';
import { globalSearchBuildUrl } from '../../../../../../shared/components/globalSearch/globalSearchBuildUrl.util';
import {
  defineRowHeight,
  getLastPathSegment,
} from '../../../../../../shared/helpers/utils/utils.helper.ts';
import { styled } from '@mui/material/styles';
import { setRowStyles } from '../../../../../../shared/components/table/styles/style.actions.ts';
import { viewRows } from './components/views/viewRows.ts';
import { QuantityEditCell } from './components/cells/quantityEdit.cell.tsx';
import { QuantityCell } from './components/cells/quantity.cell.tsx';
import { handleExportDocument } from '../../../../../../shared/components/groupTable/functions/helpers';
import type { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';

export const ArticlesOverviewTab: FC = () => {
  const { defaultValues } = useLoaderData() as ITabState;
  const [data, setData] = useState<OverviewArticleData>(defaultValues);
  const [loading, setLoading] = useState(false);
  const initIsActive = storageHelper.local.getItem('overview.articleIsGroupModeActive') ?? true;
  const initSortModel = getInitSortModel();
  const initGroupModel = ['article'];
  const [isActive, setIsActive] = useState<boolean>(initIsActive);
  const [sortModel, setSortModel] = useState<GridSortModel>(initSortModel);
  const [groupModel, setGroupModel] = useState<string[]>(initGroupModel);
  const { t } = useTranslation();
  const visibleApiRefObj = useRef<{ current: GridApiPremium } | null>(null);

  const getAllNumberRow: GridAggregationFunction<string, string | null> = {
    apply: () => {
      const count: number = data?.length || 0;
      return `${t('article.articles', { count })}: ${localeFormatterHelper.formatNumber(count, {
        precision: 0,
      })}`;
    },
    label: '',
    columnTypes: ['string'],
  };

  const formatValue = (value: number) => {
    return localeFormatterHelper.formatNumber(value, {
      returnZero: false,
    });
  };

  useEffect(() => {
    articleOverviewState.pub.init({
      articleOverview: defaultValues,
      isActiveGroupMode: initIsActive,
      sortModel: initSortModel,
    });
    const unsubData = articleOverviewState.sub
      .state()
      .subscribe(({ articleOverview, isActiveGroupMode, sortModel }) => {
        setData(articleOverview);
        setIsActive(isActiveGroupMode);
        setSortModel(sortModel);
      });
    const unsubLoading = articleOverviewState.sub.loading().subscribe(setLoading);

    return () => {
      unsubData.unsubscribe();
      unsubLoading.unsubscribe();
    };
  }, []);

  return (
    <SGroupTable
      data={data as []}
      defaultSorting={sortModel}
      onSort={(sortModel) => storageHelper.local.setItem('overview.articleSort', sortModel)}
      heightOffset={isActive ? 339 : 291}
      isActive={isActive}
      initActiveGroup={initGroupModel}
      loading={loading}
      topControls={() => (
        <SideControls
          isActive={isActive}
          onExportDocument={() =>
            handleExportDocument(visibleApiRefObj.current, {
              aggregationPosition: 'footer',
              rowsPerPage: 27,
              aggregationFunctions: {
                allNumberRow: getAllNumberRow,
                quantitySum: customSumAggregation({ returnZero: false }),
              },
            })
          }
        />
      )}
      initialState={{
        aggregation: {
          model: {
            article: 'allNumberRow',
            quantity: 'quantitySum',
          },
        },
        columns: storageHelper.local.getItem('overview.articleOverviewColumnModel'),
      }}
      onChangeColumnModel={(v) => {
        storageHelper.local.setItem('overview.articleOverviewColumnModel', v);
      }}
      aggregationFunctions={{
        allNumberRow: getAllNumberRow,
        quantitySum: customSumAggregation({ returnZero: false }),
      }}
      getRowHeight={({ model, id }) => {
        let value = model?.indTextDeliveryNote;
        if (typeof id === 'string') {
          const isIndTextDeliveryNote = id.includes('auto-generated-row-indTextDeliveryNote');
          const isIndTextProduction = id.includes('auto-generated-row-indTextProduction');
          if (isIndTextDeliveryNote || isIndTextProduction) {
            value = getLastPathSegment(id);
          }
        }
        return defineRowHeight(value);
      }}
      getApiRef={(ref) => {
        visibleApiRefObj.current = ref ?? null;
        articleOverviewState.pub.getTableApiRef(ref);
      }}
      enableSingleClickEditing={false}
      focusTabProps={{
        enabled: true,
      }}
      groupLocalStorageKey='overview.articleActiveGroup'
      groupViewRowAction={viewRows?.action}
      // Provide more width to display produced info when grouping by article
      groupColWidth={groupModel.includes('article') ? 560 : undefined}
      onGroup={(newModel) => {
        setGroupModel(newModel);
      }}
    >
      <Column
        field='article'
        headerName={t('order.article')}
        width={193}
        renderCell={(params) => {
          if (params.rowNode.type === 'group') {
            // Avoid display aggregated value in grouped row if grouping by other field than 'article'
            if (params.rowNode.groupingField !== 'article') {
              return null;
            }
            const rowIds: string[] = (params.api as any).getRowGroupChildren({
              groupId: params?.id,
            });
            const producedAmount = params.api.getRow(rowIds?.[0])?.quantityProd || 0;
            const orderedAmount: number = rowIds.reduce((sum, rowId) => {
              return sum + (params.api.getRow(rowId)?.quantity || 0);
            }, 0);
            const difference = producedAmount - orderedAmount;

            if (!producedAmount) return params.formattedValue;

            return (
              <>
                <div>{params.formattedValue}</div>
                <SInfoContainer
                  title={`${t('order.produced')}: ${producedAmount}, ${t(
                    'order.difference',
                  )}: ${difference}.`}
                >
                  <Typography variant='body2' component='span'>
                    {t('order.produced')}: {producedAmount},
                  </Typography>
                  <SDiffInfo
                    variant='body2'
                    as='span'
                    diffKind={difference >= 0 ? 'positive' : 'negative'}
                  >
                    {difference >= 0 ? (
                      <ArrowUpward fontSize='small' sx={{ verticalAlign: 'top' }} />
                    ) : (
                      <ArrowDownward fontSize='small' sx={{ verticalAlign: 'top' }} />
                    )}
                    <span>
                      {t('order.difference')}: {difference}.
                    </span>
                  </SDiffInfo>
                </SInfoContainer>
              </>
            );
          }
          return params.formattedValue;
        }}
      />
      <Column
        field='quantity'
        headerName={t('common.quantity')}
        align='right'
        headerAlign='right'
        type='number'
        width={80}
        renderEditCell={(params) => <QuantityEditCell {...params} />}
        renderCell={(params) => {
          return params?.field === '__row_group_by_columns_group__' ? (
            renderCellForGroupTable({
              params,
              headerName: t('common.quantity'),
              valueComponent: params?.value,
              sxGroupCell: { display: 'flex' },
            })
          ) : (
            <QuantityCell {...{ params }} />
          );
        }}
        editable={true}
      />
      <Column field='customerNo' headerName={t('common.customer_no')} width={91} />
      <Column field='internalOrFullName' headerName={t('common.name')} width={197} />
      <Column
        field='indTextDeliveryNote'
        headerName={t('order.text_on_delivery_note_invoice')}
        width={206}
        renderCell={(params) =>
          renderCellForGroupTable({
            params,
            valueComponent: (
              <SIndTextHtmlValue
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(params?.value ?? ''),
                }}
              />
            ),
            sxGroupCell: { display: 'flex' },
          })
        }
      />
      <Column
        field='indTextProduction'
        headerName={t('order.text_on_production_list')}
        width={213}
        renderCell={(params) =>
          renderCellForGroupTable({
            params,
            valueComponent: (
              <SIndTextHtmlValue
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(params?.value ?? ''),
                }}
              />
            ),
            sxGroupCell: { display: 'flex' },
          })
        }
      />
      <Column
        field='orderDate'
        headerName={t('common.date')}
        valueGetter={(value) => (value ? localeFormatterHelper.formatDate(value) : '')}
        width={104}
      />
      <Column
        field='orderNo'
        headerName={t('common.delivery_no')}
        width={80}
        renderCell={({ value, row }) =>
          row.orderId ? (
            <Link
              component='button'
              onClick={() =>
                navigateService.pub.navigateTo(
                  globalSearchBuildUrl('order', {
                    id: row.orderId,
                    date: row.orderDate,
                    isDeleted: false,
                    orderNo: row.orderNo,
                  }),
                )
              }
            >
              {value}
            </Link>
          ) : (
            value
          )
        }
      />
      <Column
        field='positionPrice'
        headerName={t('common.price')}
        align='right'
        headerAlign='right'
        valueFormatter={(value) => (value ? formatValue(value) : '')}
        type='number'
        width={79}
      />
      <Column
        field='positionDiscount'
        headerName={t('common.discount')}
        valueFormatter={(value) => (value ? `${value}%` : '')}
        width={63}
      />
    </SGroupTable>
  );
};

const SIndTextHtmlValue = styled('span')(() => ({
  lineHeight: 'normal',
  '&, & > p': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: 0,
  },
}));

const SGroupTable = styled(GroupTable)({
  ...setRowStyles(viewRows!.styles),
});

const SInfoContainer = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  marginLeft: 'auto',
}));

const SDiffInfo = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'diffKind',
})<IDiffInfoProps>(({ theme, diffKind }) => ({
  marginLeft: theme.spacing(2),
  color: diffKind === 'positive' ? theme.palette.success.main : theme.palette.error.main,
}));
interface IDiffInfoProps {
  diffKind: 'positive' | 'negative';
}
